import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { Reveal, EASE_EDITORIAL } from "../components/Reveal";
import { PROJECTS } from "../data/projects";

// Asymmetric placement per project — exhibition wall, not a grid.
const PLACEMENT = [
  { wrap: "lg:col-span-7 lg:col-start-1", offset: "" },
  { wrap: "lg:col-span-6 lg:col-start-7", offset: "lg:mt-40" },
  { wrap: "lg:col-span-5 lg:col-start-2", offset: "lg:mt-24" },
  { wrap: "lg:col-span-7 lg:col-start-6", offset: "lg:mt-32" },
];

const ProjectEntry = ({ project, place }) => {
  const isFilm = project.type === "film";
  return (
    <div className={`${place.wrap} ${place.offset}`}>
      <Reveal y={40}>
        <Link
          to={`/work/${project.slug}`}
          data-testid={`project-link-${project.slug}`}
          className="group block"
        >
          <div className="mb-5 flex items-baseline justify-between border-b border-line pb-3">
            <span className="text-[0.7rem] uppercase tracking-[0.24em] text-gold">
              {project.index}
            </span>
            <span className="text-[0.7rem] uppercase tracking-[0.22em] text-ink-soft">
              {project.medium} — {project.year}
            </span>
          </div>

          <div className="relative overflow-hidden">
            {isFilm ? (
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-noir">
                <motion.img
                  src={project.cover}
                  alt={project.title}
                  className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-1000 ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-noir/85 via-noir/25 to-noir/40" />
                <div className="absolute left-6 top-6">
                  <span className="text-[0.7rem] uppercase tracking-[0.24em] text-ivory/70">
                    Experimental Film
                  </span>
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full border border-ivory/70 backdrop-blur-sm transition-colors duration-500 group-hover:border-gold group-hover:bg-ivory/10">
                    <Play className="ml-0.5 h-5 w-5 text-ivory" fill="currentColor" />
                  </span>
                  <span className="text-[0.7rem] uppercase tracking-[0.24em] text-ivory/85">
                    Watch the film
                  </span>
                </div>
              </div>
            ) : (
              <motion.img
                src={project.cover}
                alt={project.title}
                className="aspect-[4/5] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
              />
            )}
          </div>

          <div className="mt-6 flex items-end justify-between">
            <h3 className="font-serif text-4xl font-light leading-none tracking-tight text-ink transition-colors duration-500 group-hover:text-oxblood sm:text-5xl">
              {project.title}
            </h3>
            <span className="mb-1 hidden text-[0.7rem] uppercase tracking-[0.22em] text-ink-soft transition-all duration-500 group-hover:text-oxblood sm:inline">
              Open ↗
            </span>
          </div>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-ink-soft">
            {project.concept}
          </p>
        </Link>
      </Reveal>
    </div>
  );
};

const WorkArchive = () => {
  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative scroll-mt-24 border-t border-line bg-surface px-6 py-28 sm:px-10 sm:py-40"
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-20 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <Reveal>
              <span className="text-[0.7rem] uppercase tracking-[0.28em] text-gold">
                002 — From The Kitchen
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-serif text-5xl font-light leading-[0.92] tracking-tight text-ink sm:text-6xl lg:text-7xl">
                Find Some <span className="italic">Recipes</span> Here
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-sm leading-relaxed text-ink-soft">
              These are a few of my favourite things I've cooked — shoots and films made for
              no reason other than wanting to. Pour yourself something, take your time, and
              have a look. Each one opens into its own little world.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 gap-x-12 gap-y-24 lg:grid-cols-12 lg:gap-y-8">
          {PROJECTS.map((p, i) => (
            <ProjectEntry key={p.slug} project={p} place={PLACEMENT[i]} />
          ))}
        </div>
      </div>
    </section>
  );
};

export const Marquee = () => (
  <div className="overflow-hidden border-y border-line bg-ivory py-6">
    <div className="marquee-track flex w-max whitespace-nowrap">
      {[0, 1].map((k) => (
        <span key={k} className="flex items-center" aria-hidden={k === 1}>
          {["Concept", "Styling", "Creative Direction", "Storytelling", "Film", "Editorial"].map(
            (t) => (
              <span key={t} className="flex items-center">
                <span className="px-8 font-serif text-3xl italic text-ink sm:text-4xl">{t}</span>
                <span className="text-gold">✦</span>
              </span>
            )
          )}
        </span>
      ))}
    </div>
  </div>
);

export { EASE_EDITORIAL };
export default WorkArchive;
